/**
 * O2 Product Card Component
 *
 * Handles interactivity for O2-branded product cards including:
 * - Compare functionality
 * - Wishlist functionality  
 * - Quick add to cart with AJAX
 * - Local storage for comparison and wishlist
 *
 * @author O2 Theme Development
 * @version 1.0.0
 */

const O2Store = {
  getList(key) {
    try {
      return JSON.parse(localStorage.getItem(key)) ?? [];
    } catch {
      return [];
    }
  },
  setList(key, list) {
    try {
      localStorage.setItem(key, JSON.stringify(list));
    } catch (e) {
      console.error(e);
    }
  },
  removeList(key) {
    localStorage.removeItem(key);
  },
};

if (!customElements.get('product-card-o2')) {
  class ProductCardO2 extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.compareBtn = this.querySelector('.o2-card__action-btn--compare');
      this.wishlistBtn = this.querySelector('.o2-card__action-btn--wishlist');
      this.addToCartBtn = this.querySelector('.o2-card__add-to-cart');
      this.cardAddError = this.querySelector('[data-o2-card-add-error]');
      this.cardAddErrorText = this.querySelector('[data-o2-card-add-error-text]');

      this.productId = this.addToCartBtn?.dataset.productId;

      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      this.init();
    }

    init() {
      // Initialize comparison button
      if (this.compareBtn) {
        this.updateCompareState();
        this.compareBtn.addEventListener('click', this.handleCompare.bind(this));
      }

      // Initialize wishlist button
      if (this.wishlistBtn) {
        this.updateWishlistState();
        this.wishlistBtn.addEventListener('click', this.handleWishlist.bind(this));
      }

      // Initialize add to cart button
      if (this.addToCartBtn) {
        this.addToCartBtn.addEventListener('click', this.handleAddToCart.bind(this));
      }
    }

    getAddErrorMessage() {
      return this.addToCartBtn?.dataset.addErrorMessage || 'Unable to add to cart. Please try again.';
    }

    getCampaignDiscountCode() {
      return String(
        this.querySelector('[data-o2-campaign-discount-code]')?.dataset.o2CampaignDiscountCode || ''
      ).trim();
    }

    getCampaignDiscountErrorMessage() {
      return (
        this.addToCartBtn?.dataset.campaignDiscountError ||
        'The product was added to the cart, but the discount code could not be applied.'
      );
    }

    resolveCartAddErrorMessage(responseJson, fallbackMessage = '') {
      const description = typeof responseJson?.description === 'string' ? responseJson.description.trim() : '';
      if (description) return description;

      const message = typeof responseJson?.message === 'string' ? responseJson.message.trim() : '';
      if (message) return message;

      const errors = responseJson?.errors;
      if (typeof errors === 'string' && errors.trim()) return errors.trim();

      if (errors && typeof errors === 'object') {
        const errorValues = Object.values(errors)
          .map((value) => {
            if (Array.isArray(value)) return value.join(' ');
            if (typeof value === 'string') return value;
            return '';
          })
          .map((value) => value.trim())
          .filter(Boolean);

        if (errorValues.length) return errorValues[0];
      }

      return fallbackMessage;
    }

    setCardErrorMessage(message, { fallbackToNotification = true } = {}) {
      const canRenderInCard = this.cardAddError instanceof HTMLElement && this.cardAddErrorText instanceof HTMLElement;

      if (!canRenderInCard) {
        if (message && fallbackToNotification) this.showNotification(message, 'error');
        return;
      }

      if (!message) {
        this.cardAddErrorText.textContent = '';
        this.cardAddError.hidden = true;
        return;
      }

      this.cardAddErrorText.textContent = message;
      this.cardAddError.hidden = false;
    }

    /**
     * Handle product comparison
     */
    handleCompare(e) {
      e.preventDefault();
      e.stopPropagation();

      if (!this.productId) return;

      const compareList = this.getCompareList();
      const index = compareList.indexOf(this.productId);

      if (index > -1) {
        // Remove from comparison
        compareList.splice(index, 1);
        this.showNotification('Removed from comparison', 'info');
      } else {
        // Add to comparison (max 4 products)
        if (compareList.length >= 4) {
          this.showNotification('Maximum 4 products can be compared', 'warning');
          return;
        }
        compareList.push(this.productId);
        this.showNotification('Added to comparison', 'success');
      }

      this.saveCompareList(compareList);
      this.updateCompareState();
      this.dispatchCompareEvent();
    }

    /**
     * Handle wishlist functionality
     */
    handleWishlist(e) {
      e.preventDefault();
      e.stopPropagation();

      if (!this.productId) return;

      const wishlist = this.getWishlist();
      const index = wishlist.indexOf(this.productId);

      if (index > -1) {
        // Remove from wishlist
        wishlist.splice(index, 1);
        this.showNotification('Removed from wishlist', 'info');
      } else {
        // Add to wishlist
        wishlist.push(this.productId);
        this.showNotification('Added to wishlist', 'success');
      }

      this.saveWishlist(wishlist);
      this.updateWishlistState();
      this.dispatchWishlistEvent();
    }

    /**
     * Handle add to cart with AJAX
     */
    async handleAddToCart(e) {
      e.preventDefault();
      e.stopPropagation();

      if (!this.productId || this.addToCartBtn.disabled) return;

      // Get first available variant
      const variantId = this.addToCartBtn.dataset.variantId || this.productId;

      const priceAtAdd = this.addToCartBtn.dataset.priceAtAdd;
      if (!o2IsValidPriceAtAdd(priceAtAdd)) {
        console.warn('[product-card-o2] Missing or invalid data-price-at-add on add button.', this.addToCartBtn);
        this.setCardErrorMessage(this.getAddErrorMessage());
        return;
      }

      this.setCardErrorMessage('');

      // Show loading state
      this.addToCartBtn.classList.add('loading');
      this.addToCartBtn.disabled = true;

      // Prepare FormData like product-form.js
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);
      formData.append('properties[_o2_price_at_add]', priceAtAdd);

      let sections = [];
      if (this.cart) {
        sections = this.cart.getSectionsToRender().map((section) => section.id);
        formData.append('sections', sections.join(','));
        formData.append('sections_url', window.location.pathname);
      }

      try {
        const config = {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/javascript',
          },
          body: formData,
        };

        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', config);

        let responseJson;
        try {
          responseJson = await response.json();
        } catch (parseError) {
          if (!response.ok) {
            throw new Error(this.getAddErrorMessage());
          }
          throw parseError;
        }

        if (!response.ok || responseJson?.status) {
          throw new Error(this.resolveCartAddErrorMessage(responseJson, this.getAddErrorMessage()));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-card-o2',
          productVariantId: variantId,
          cartData: responseJson,
        }).catch(console.error);

        let data = responseJson;
        let campaignFailed = false;
        const campaignDiscountCode = this.getCampaignDiscountCode();

        if (campaignDiscountCode) {
          try {
            if (!window.O2CampaignDiscount) {
              throw new Error('Campaign discount helper is unavailable.');
            }

            const discountResult = await window.O2CampaignDiscount.apply([campaignDiscountCode], { sections });
            campaignFailed = discountResult.unappliedCodes.some(
              (code) => code.toLowerCase() === campaignDiscountCode.toLowerCase()
            );
            if (campaignFailed) {
              const errorMessage = this.getCampaignDiscountErrorMessage();
              this.setCardErrorMessage(errorMessage);
              window.O2CampaignDiscount.showError(errorMessage);
            }
            if (discountResult.cartState?.sections) {
              data = { ...responseJson, sections: discountResult.cartState.sections };
            }
          } catch (error) {
            console.error('Campaign discount error:', error);
            campaignFailed = true;
            const errorMessage = this.getCampaignDiscountErrorMessage();
            this.setCardErrorMessage(errorMessage);
            window.O2CampaignDiscount?.showError(errorMessage);
          }
        }

        // Update cart count
        this.updateCartCount();

        // Render cart contents and open drawer
        if (this.cart) {
          this.cart.renderContents(data);
        }

        // Show success notification
        // this.showNotification('Added to cart', 'success');

        // Trigger cart drawer/notification events
        this.dispatchCartEvent(data);
      } catch (error) {
        console.error('Add to cart error:', error);
        this.setCardErrorMessage(error?.message || this.getAddErrorMessage());
      } finally {
        // Remove loading state
        this.addToCartBtn.classList.remove('loading');
        this.addToCartBtn.disabled = false;
        await window.O2CartOrderLimit?.syncCartState?.();
      }
    }

    getCompareList() {
      return O2Store.getList('o2-compare-list');
    }
    saveCompareList(list) {
      O2Store.setList('o2-compare-list', list);
    }
    getWishlist() {
      return O2Store.getList('o2-wishlist');
    }
    saveWishlist(list) {
      O2Store.setList('o2-wishlist', list);
    }

    /**
     * Update compare button state
     */
    updateCompareState() {
      if (!this.compareBtn || !this.productId) return;

      const compareList = this.getCompareList();
      const isInCompare = compareList.includes(this.productId);

      this.compareBtn.classList.toggle('active', isInCompare);
      this.compareBtn.setAttribute('aria-pressed', isInCompare);
    }

    /**
     * Update wishlist button state
     */
    updateWishlistState() {
      if (!this.wishlistBtn || !this.productId) return;

      const wishlist = this.getWishlist();
      const isInWishlist = wishlist.includes(this.productId);

      this.wishlistBtn.classList.toggle('active', isInWishlist);
      this.wishlistBtn.setAttribute('aria-pressed', isInWishlist);

      // Fill heart icon when in wishlist
      const svg = this.wishlistBtn.querySelector('svg path');
      if (svg) {
        if (isInWishlist) {
          svg.setAttribute('fill', 'currentColor');
        } else {
          svg.setAttribute('fill', 'none');
        }
      }
    }

    /**
     * Update cart count in header
     */
    async updateCartCount() {
      try {
        const response = await fetch(window.Shopify.routes.root + 'cart.js');
        const cart = await response.json();

        // Update cart count elements
        document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
          bubble.textContent = cart.item_count;
          bubble.classList.remove('hidden');
        });
      } catch (e) {
        console.error('Failed to update cart count:', e);
      }
    }

    /**
     * Show notification to user
     */
    showNotification(message, type = 'info') {
      // Dispatch event that can be caught by notification system
      const event = new CustomEvent('o2:notification', {
        detail: { message, type },
        bubbles: true,
      });
      this.dispatchEvent(event);

      // Fallback: console log
      console.log(`[${type.toUpperCase()}] ${message}`);
    }

    /**
     * Dispatch compare list change event
     */
    dispatchCompareEvent() {
      const event = new CustomEvent('o2:compare-updated', {
        detail: { compareList: this.getCompareList() },
        bubbles: true,
      });
      document.dispatchEvent(event);
    }

    /**
     * Dispatch wishlist change event
     */
    dispatchWishlistEvent() {
      const event = new CustomEvent('o2:wishlist-updated', {
        detail: { wishlist: this.getWishlist() },
        bubbles: true,
      });
      document.dispatchEvent(event);
    }

    /**
     * Dispatch cart update event
     */
    dispatchCartEvent(item) {
      const event = new CustomEvent('o2:cart-updated', {
        detail: { item },
        bubbles: true,
      });
      document.dispatchEvent(event);

      // Also trigger standard Shopify cart event
      document.dispatchEvent(new CustomEvent('cart:item-added'));
    }
  }

  customElements.define('product-card-o2', ProductCardO2);
}

/**
 * Public API for accessing compare and wishlist data from outside the component.
 */
window.O2ProductCard = {
  getCompareList() {
    return O2Store.getList('o2-compare-list');
  },
  getWishlist() {
    return O2Store.getList('o2-wishlist');
  },
  clearCompareList() {
    O2Store.removeList('o2-compare-list');
    document.dispatchEvent(new CustomEvent('o2:compare-updated', { detail: { compareList: [] } }));
  },
  clearWishlist() {
    O2Store.removeList('o2-wishlist');
    document.dispatchEvent(new CustomEvent('o2:wishlist-updated', { detail: { wishlist: [] } }));
  },
};
